// MULTISITIO (Diego, 13-oct, segunda tanda): «no sé dónde se selecciona AL INICIO, al crear una cotización nueva, que van a
// ser varios proyectos (Crazy Motors tiene 2 sucursales) para que salgan en la misma propuesta». La pregunta va en el primer
// paso de «Nueva cotización», debajo del nombre del cliente. Con «Varios sitios» se escriben los nombres de las sucursales y
// al crear sale una cotización por sucursal, todas del mismo proyecto (cada una con sus recibos, su tarifa y sus opciones).
// ADENDA z65 (30-oct): cada sucursal desde la 2 lleva SU tipo de cliente y SU tarifa (la casa del dueño incluida); por omisión,
// «igual que la sucursal 1» (lo de antes). La lógica vive en logica/sitios.ts.
import { Plus, Trash2 } from 'lucide-react';
import { SITIOS_MAX } from '@cotizador/compartido';
import type { Tarifa, TipoCliente } from '@cotizador/compartido';
import { Boton, CampoSelect, CampoTexto, Segmentado } from '../../componentes/ui.tsx';
import { ETIQUETA_TIPO_SITIO, TIPOS_SITIO, opcionesTarifa, sitioVacio, tarifaCompatible } from '../../logica/sitios.ts';
import type { SitioForma, SitiosForma, TipoSitio } from '../../logica/sitios.ts';

export type { SitiosForma } from '../../logica/sitios.ts';
export { problemaSitios } from '../../logica/sitios.ts';

type Principal = { tipo: TipoCliente | null; tarifa: Tarifa | 'no_se' };

export function PreguntaSitios({ sitios, alCambiar, error, principal }: { sitios: SitiosForma; alCambiar: (s: SitiosForma) => void; error: string | null; principal?: Principal }) {
  return (
    <div className="pila">
      <Segmentado<'uno' | 'varios'>
        etiqueta="¿Cuántos sitios lleva la propuesta?"
        valor={sitios ? 'varios' : 'uno'}
        alCambiar={(v) => alCambiar(v === 'varios' ? (sitios ?? [sitioVacio(), sitioVacio()]) : null)}
        opciones={[
          { valor: 'uno', texto: 'Un solo sitio', detalle: 'Un recibo o un medidor (lo de siempre)' },
          { valor: 'varios', texto: 'Varios sitios (sucursales)', detalle: 'P. ej. 2 sucursales del mismo cliente, cada una con su recibo' },
        ]}
        columnas={2}
        ayuda={sitios ? 'Se crea una cotización por sucursal, todas del mismo proyecto: cada una lleva sus recibos, su tarifa y sus opciones.' : undefined}
      />
      {sitios && <NombresSitios sitios={sitios} alCambiar={alCambiar} error={error} principal={principal} />}
    </div>
  );
}

/** Las sucursales: nombre, y desde la 2 su tipo de cliente y su tarifa (13-oct, 22:45; z65 el 30-oct). */
export function NombresSitios({ sitios, alCambiar, error, principal }: { sitios: SitioForma[]; alCambiar: (s: SitiosForma) => void; error: string | null; principal?: Principal }) {
  const poner = (i: number, cambio: Partial<SitioForma>) => alCambiar(sitios.map((x, j) => (j === i ? { ...x, ...cambio } : x)));
  const tipo1 = principal?.tipo ?? null;
  return (
    <div className="pila">
      {sitios.map((s, i) => {
        const tipo = s.tipo ?? tipo1;
        const igualTipo = tipo === tipo1;
        return (
          <div className="pila sitio-renglon" key={i}>
            <div className="rejilla-2">
              <CampoTexto
                etiqueta={`Sucursal ${i + 1}`}
                placeholder={i === 0 ? 'P. ej. Sucursal Norte' : 'P. ej. Sucursal Sur o Casa del dueño'}
                maxLength={40}
                autoComplete="off"
                value={s.nombre}
                onChange={(e) => poner(i, { nombre: e.target.value })}
              />
              {sitios.length > 2 && (
                <div>
                  <Boton chico variante="secundario" icono={<Trash2 aria-hidden />} onClick={() => alCambiar(sitios.filter((_, j) => j !== i))}>
                    Quitar sucursal {i + 1}
                  </Boton>
                </div>
              )}
            </div>
            {i === 0 ? (
              <p className="nota-suave">
                Tipo y tarifa: los del cuestionario{tipo1 ? ` (${ETIQUETA_TIPO_SITIO[tipo1]}${principal?.tarifa && principal.tarifa !== 'no_se' ? ` · ${principal.tarifa}` : ''})` : ''}.
              </p>
            ) : (
              <div className="rejilla-2">
                <CampoSelect
                  etiqueta={`Tipo de la sucursal ${i + 1}`}
                  value={s.tipo ?? ''}
                  opciones={[{ valor: '', texto: 'Igual que la sucursal 1' }, ...TIPOS_SITIO.map((t) => ({ valor: t, texto: ETIQUETA_TIPO_SITIO[t] }))]}
                  onChange={(e) => {
                    const t = (e.target.value || null) as TipoSitio | null;
                    const nueva = t ?? tipo1;
                    poner(i, { tipo: t, tarifa: s.tarifa && nueva && !tarifaCompatible(nueva, s.tarifa) ? null : s.tarifa });
                  }}
                />
                <CampoSelect
                  etiqueta={`Tarifa de la sucursal ${i + 1}`}
                  value={s.tarifa ?? (igualTipo ? '' : 'no_se')}
                  opciones={igualTipo ? [{ valor: '', texto: 'Igual que la sucursal 1' }, ...opcionesTarifa(tipo, s.tarifa)] : opcionesTarifa(tipo, s.tarifa)}
                  onChange={(e) => poner(i, { tarifa: (e.target.value || null) as Tarifa | 'no_se' | null })}
                />
              </div>
            )}
          </div>
        );
      })}
      {sitios.length < SITIOS_MAX && (
        <div>
          <Boton chico variante="secundario" icono={<Plus aria-hidden />} onClick={() => alCambiar([...sitios, sitioVacio()])}>
            Agregar otra sucursal
          </Boton>
        </div>
      )}
      {error && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
